// Detecting a single contact record that actually encodes TWO+ people —
// the "Anthony Baker, Holly Baker" / "Anthony & Holly Baker" /
// "Melissa Tapia and Emiliano Tapia" artifact that insurance bulk
// imports created by jamming a husband+wife cell into one contact.
//
// Baker (2026-06-27 Loom) flagged that the capitalization detector's
// one-click recase "fixes a fake issue" on these — it title-cases the
// name but the record is still two people in one. Grant's fear: fixing
// the cosmetic issue removes the contact from Cleanup and the REAL
// structural problem is lost forever. This predicate is the single
// shared truth used BOTH to surface these as their own Cleanup issue
// (the combined-contact detector) AND to stop name-caps from offering a
// masking recase on them (the suppression). One predicate, no drift.
//
// Precision notes (these flags are review-only — they recommend opening
// the record, never a destructive one-click — so a rare false positive
// just costs a human a glance + dismiss; we therefore lean inclusive on
// the strong signals and careful on the ambiguous ones):
//   • A HYPHEN alone is NOT a signal: "Comer-Lopez" is a legitimate
//     compound surname, not two people. A hyphen only counts as evidence
//     when a name TOKEN repeats (rule 3 below).
//   • A repeated name token (≥3 letters) is a strong tell of two people
//     sharing a surname: "Anthony Baker … Holly Baker" → "baker" twice.
//     A single real person's name effectively never repeats a word.
//   • A comma is "Last, First" for ONE person when each side is a single
//     word ("Baker, Anthony"); it means two people only when both sides
//     are multi-word ("Anthony Baker, Holly Baker") or there are 3+ comma
//     chunks ("Miranda, Andre, Andre M").

export interface ContactNameParts {
  legal_first_name?: string | null;
  legal_middle_name?: string | null;
  legal_last_name?: string | null;
  preferred_name?: string | null;
}

// Lowercased alphabetic tokens of a name, split on every character a
// combined record uses to glue two names together (whitespace, comma,
// slash, ampersand, plus, hyphen). Used for the repeated-token tell.
function nameTokens(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[\s,/&+\-]+/)
    .map((t) => t.replace(/[^a-z]/g, ""))
    .filter((t) => t.length > 0);
}

// A ≥3-letter token appearing twice → two people sharing a surname
// (or, rarely, a first name). Catches the cases with NO explicit
// separator ("Anthony Baker Holly Baker") and the hyphen case
// ("Anthony Baker-Holly Baker") that we deliberately don't treat as a
// separator on its own.
function hasRepeatedNameToken(name: string): boolean {
  const seen = new Set<string>();
  for (const t of nameTokens(name)) {
    if (t.length < 3) continue;
    if (seen.has(t)) return true;
    seen.add(t);
  }
  return false;
}

// Does this single name string look like it encodes more than one
// person? See the file header for the precision rationale per rule.
export function looksLikeMultiplePeople(raw?: string | null): boolean {
  if (!raw) return false;
  const name = raw.replace(/\s+/g, " ").trim();
  if (!name) return false;

  // 1) Strong "joins two names" separators. '&'/'+' mean "and"; a slash
  //    or "w/" separates two people; " and " (as a whole word, with
  //    spaces — never matches inside "Sandra"/"Anderson") joins two
  //    names.
  if (/[&+]/.test(name)) return true;
  if (/\//.test(name)) return true;
  if (/\sw\/\s/i.test(name)) return true;
  if (/\sand\s/i.test(name)) return true;

  // 2) Comma structure. "Baker, Anthony" (single word each side) is the
  //    Last, First form for ONE person and must NOT flag. Two people:
  //    3+ chunks, or exactly 2 chunks each of which is multi-word.
  const chunks = name
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (chunks.length >= 3) return true;
  if (
    chunks.length === 2 &&
    chunks.every((c) => c.split(/\s+/).filter(Boolean).length >= 2)
  ) {
    return true;
  }

  // 3) A repeated surname/token across the whole name. Catches the
  //    hyphen + no-separator cases ("Anthony Baker-Holly Baker",
  //    "Anthony Baker Holly Baker") without treating a plain compound
  //    surname ("Comer-Lopez") as two people.
  if (hasRepeatedNameToken(name)) return true;

  return false;
}

// The combined legal name (first + middle + last), space-joined.
export function fullContactName(p: ContactNameParts): string {
  return [p.legal_first_name, p.legal_middle_name, p.legal_last_name]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

// The contact-level predicate used by BOTH the combined-contact detector
// and the name-caps suppression. Evaluates the COMBINED legal name first
// (so a surname repeated across first+last — first="Anthony Baker",
// last="Holly Baker" — is caught), then the preferred name on its own
// (a combined record sometimes lives only there).
export function contactNameLooksMultiPerson(p: ContactNameParts): boolean {
  return (
    looksLikeMultiplePeople(fullContactName(p)) ||
    looksLikeMultiplePeople(p.preferred_name)
  );
}

// Best-effort split of a combined name into the individual people, for
// DISPLAY only (evidence lines). Splits on the explicit separators; when
// there's no explicit separator (the repeated-token / hyphen case) it
// returns the whole string as one part and the caller falls back to
// showing the raw name.
export function splitPersonNames(raw?: string | null): string[] {
  return splitPersonNameParts(raw);
}

// One parsed-out person from a combined record, ready to become their
// own contact row. `lastName` may be inferred (see below).
export interface SplitPersonName {
  firstName: string;
  lastName: string | null;
}

// Donna 2026-07-30 — parse a combined name into EXACTLY TWO people with
// first/last structure, for the one-click "Split into two contacts"
// action. Strict on purpose: this feeds a WRITE (the display-only
// splitPersonNames above feeds evidence lines and can afford to be
// loose). Returns null whenever the parse isn't confident — the caller
// then steers the human to edit the contact by hand instead of writing
// a guessed name.
//
// The shared-surname inference is the load-bearing piece: the dominant
// artifact shape is "Carie & Eddie D'Hondt" — the FIRST person carries
// no surname because the couple shares one. A single-word part borrows
// the OTHER part's last token as its surname. When neither part has a
// surname ("Carie & Eddie") there's nothing safe to infer → null.
export function splitCombinedNamePair(
  raw?: string | null,
): [SplitPersonName, SplitPersonName] | null {
  const parts = splitPersonNameParts(raw);
  // Exactly two people — 3+ chunks ("Miranda, Andre, Andre M") are too
  // ambiguous to auto-split; one chunk means no explicit separator.
  if (parts.length !== 2) return null;

  const tokenized = parts.map((p) => p.split(/\s+/).filter(Boolean));
  const [a, b] = tokenized;
  if (!a || !b || a.length === 0 || b.length === 0) return null;
  // Anything longer than "First Middle Last" on a side is beyond what
  // we can structure confidently.
  if (a.length > 3 || b.length > 3) return null;

  const person = (tokens: string[], otherTokens: string[]): SplitPersonName | null => {
    if (tokens.length >= 2) {
      return {
        firstName: tokens.slice(0, -1).join(" "),
        lastName: tokens[tokens.length - 1] ?? null,
      };
    }
    // Single word — borrow the other side's surname (shared-surname
    // couple). If the other side is also a single word, give up.
    if (otherTokens.length >= 2) {
      return {
        firstName: tokens[0]!,
        lastName: otherTokens[otherTokens.length - 1] ?? null,
      };
    }
    return null;
  };

  const p1 = person(a, b);
  const p2 = person(b, a);
  if (!p1 || !p2) return null;
  // Identical people ("Baker & Baker") — nothing meaningful to split.
  if (
    p1.firstName.toLowerCase() === p2.firstName.toLowerCase() &&
    (p1.lastName ?? "").toLowerCase() === (p2.lastName ?? "").toLowerCase()
  ) {
    return null;
  }
  return [p1, p2];
}

function splitPersonNameParts(raw?: string | null): string[] {
  if (!raw) return [];
  const name = raw.replace(/\s+/g, " ").trim();
  if (!name) return [];
  const parts = name
    .split(/\s*(?:&|\+|\band\b|\sw\/\s|\/|,)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(p);
    }
  }
  return out;
}
